<script setup lang="ts">
import { useBillingMeQuery } from '~/api/queries/billing';
import { readUaeProgramme } from '~/utils/uae-gate-client/uaeGate';

// UAE next-step sheet (ID-106). Mounted once in the default layout; also keeps the
// shared UAE programme state in sync with /api/billing/me.
const { t } = useI18n();
const { data: billingMe } = useBillingMeQuery();
const { sheet, setProgramme, trackCta, close } = useUaeGate();

watch(
  () => billingMe.value,
  (me) => setProgramme(readUaeProgramme(me)),
  { immediate: true },
);

const onCta = async () => {
  const target = sheet.value?.ctaRoute;
  trackCta(sheet.value?.variant ?? 'not_now');
  close();
  if (target) await navigateTo(target);
};

const onNotNow = () => {
  trackCta('not_now');
  close();
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="sheet"
      class="fixed inset-0 z-[1000] flex items-end justify-center tablet:items-center"
    >
      <div
        class="absolute inset-0 bg-black-100/50"
        @click="onNotNow"
      />
      <div
        class="uae-gate relative w-full max-w-[440px] rounded-t-3xl bg-white px-6 pb-8 pt-7 text-center shadow-2xl tablet:rounded-3xl"
        role="dialog"
        aria-modal="true"
        :aria-label="t(sheet.titleKey)"
      >
        <span
          class="mx-auto mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-gold-primary-100/15 text-gold-primary-120"
          aria-hidden="true"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="4"
              y="10"
              width="16"
              height="11"
              rx="2"
            />
            <path d="M8 10V7a4 4 0 1 1 8 0v3" />
          </svg>
        </span>
        <h2 class="font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.02em] text-black-100">
          {{ t(sheet.titleKey) }}
        </h2>
        <p
          v-if="sheet.limitLine"
          class="mt-3 font-mono text-[12px] font-bold uppercase tracking-[0.1em] text-gold-primary-120"
        >
          {{ t(sheet.limitLine.key, { count: sheet.limitLine.count }) }}
        </p>
        <p class="mt-3 text-[15px] leading-[1.5] text-black-60">
          {{ t(sheet.bodyKey) }}
        </p>
        <div class="mt-7 flex flex-col gap-2.5">
          <button
            type="button"
            class="inline-flex h-12 w-full items-center justify-center rounded-xl bg-gold-primary-100 px-6 text-[15px] font-semibold text-black-100 transition-colors hover:bg-gold-primary-120"
            @click="onCta"
          >
            {{ t(sheet.ctaKey) }}
          </button>
          <button
            type="button"
            class="inline-flex h-11 w-full items-center justify-center rounded-xl text-[14px] font-semibold text-black-60 transition-colors hover:bg-black-03"
            @click="onNotNow"
          >
            {{ t('billing.uaeGate.notNow') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.uae-gate {
  padding-bottom: max(2rem, env(safe-area-inset-bottom));
}
</style>
